// src/components/AddressBarStory.jsx
import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import './AddressBarStory.css';

const AddressBarStory = forwardRef((props, ref) => {
  const [isRunning, setIsRunning] = useState(false);
  const [currentWord, setCurrentWord] = useState('');
  const [showIndicator, setShowIndicator] = useState(false);
  const timeoutRef = useRef(null);
  const storyIndexRef = useRef(0);

  // Use ref for isRunning to avoid stale closures in timeouts
  const isRunningRef = useRef(false);

  // YOUR STORY TEXT
  const STORY_TEXT = `Canto 1.
I was the shadow of the waxwing slain,

By the false azure in MS windows pane;

I was the smudge of ashen fluff and I,

Lived on flew on in the reflected sky,

And from the inside too Id duplicate,

Myself my lamp an apple on a plate,

Un curtaining the night Id let dark glass,

Hang all the furniture above the grass,

And how delightful when a fall of snow,

Covered my glimpse of lawn and reached up so,

As to make chair and bed exactly stand,

Upon that snow, out in that crystal land!

Retake the falling snow: each drifting flake,

Shapeless and slow, unsteady and opaque,

A dull dark white against the day's pale white,

And abstract larches in the neutral light.

And then the gradual and dual blue,

As night unites the viewer and the view,

And in the morning, diamonds of frost,

Express amazement: Whose spurred feet have crossed,

From left to right the blank page of the road?

Reading from left to right in winter's code:

A dot, an arrow pointing back; repeat:

Dot, arrow pointing back... A pheasants feet!

Torquated beauty, sublimated grouse,

Finding your China right behind my house.

Was he in Sherlock Holmes, the fellow whose

Tracks pointed back when he reversed his shoes?

unlock the artefact with the pheasants feet`;

  // Parse story into words
  const storyWords = React.useMemo(() => {
    const words = STORY_TEXT.split(/\s+/).filter(word => word.trim() !== '');
    console.log('📋 Parsed story words:', words);
    console.log('🔢 Total words:', words.length);
    return words;
  }, []);

  // Keep ref in sync with state
  useEffect(() => {
    isRunningRef.current = isRunning;
  }, [isRunning]);

  // Define stopStory first
  const stopStory = useCallback(() => {
    console.log('🛑 Stopping story');
    setIsRunning(false);
    isRunningRef.current = false;
    setShowIndicator(false);
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // Clear URL after delay
    setTimeout(() => {
      console.log('🧹 Cleaning URL');
      window.history.replaceState({}, '', window.location.pathname + window.location.search);
    }, 1000);
  }, []);

  // Now define completeStory
  const completeStory = useCallback(() => {
    console.log('✨ Story complete');
    setTimeout(() => {
      stopStory();
    }, 3000);
  }, [stopStory]);

  // Create refs for callbacks
  const stopStoryRef = useRef(() => {});
  const completeStoryRef = useRef(() => {});

  // Update the refs
  useEffect(() => {
    stopStoryRef.current = stopStory;
    completeStoryRef.current = completeStory;
  }, [stopStory, completeStory]);

  const updateAddressBar = useCallback((word) => {
    console.log('📝 Updating URL hash:', word);
    
    try {
      const newHash = `#story/${storyIndexRef.current}/${encodeURIComponent(word)}`;
      console.log('🔗 New hash:', newHash);
      
      window.history.replaceState(
        { storyIndex: storyIndexRef.current, storyWord: word },
        '',
        newHash
      );
      
      console.log('✅ replaceState called. Current URL:', window.location.href);
    } catch (error) {
      console.error('❌ Error updating address bar:', error);
    }
  }, []);

  const showNextWord = useCallback(() => {
    console.log('➡️ showNextWord - Index:', storyIndexRef.current, 'Running (ref):', isRunningRef.current);
    
    // Use the ref instead of state to avoid stale closures
    if (!isRunningRef.current) {
      console.log('⏹️ Not running, stopping');
      return;
    }
    
    if (storyIndexRef.current >= storyWords.length) {
      console.log('🏁 Reached end of story');
      completeStoryRef.current();
      return;
    }

    const word = storyWords[storyIndexRef.current];
    console.log('📖 Current word:', word);
    
    // Update the address bar
    updateAddressBar(word);
    
    // Update current word for indicator
    setCurrentWord(word);
    
    // Calculate delay
    let delay = 350;
    
    // Add extra pause for punctuation
    if (/[.,!?]$/.test(word)) {
      delay += 500;
      console.log('⏸️ Punctuation pause');
    } else if (/[;:]$/.test(word)) {
      delay += 200;
    }

    console.log(`⏱️ Waiting ${delay}ms for next word`);
    
    // Schedule next word
    timeoutRef.current = setTimeout(() => {
      storyIndexRef.current++;
      showNextWord();
    }, delay);
  }, [storyWords, updateAddressBar]);

  const startStory = useCallback(() => {
    console.log('📖 Starting address bar story...');
    
    // If already running, stop it
    if (isRunningRef.current) {
      console.log('⏸️ Already running, stopping first');
      stopStoryRef.current();
      return;
    }
    
    // Set both state and ref
    setIsRunning(true);
    isRunningRef.current = true;
    setShowIndicator(true);
    storyIndexRef.current = 0;
    
    // Small delay before starting to ensure state is updated
    setTimeout(() => {
      console.log('🚀 Starting word sequence. Running state:', isRunning, 'Running ref:', isRunningRef.current);
      showNextWord();
    }, 100); // Short delay to ensure state updates
  }, [showNextWord, isRunning]); // Keep isRunning in deps but use ref in logic

  // Check URL on mount to resume story
  useEffect(() => {
    console.log('🔍 Checking URL on mount:', window.location.hash);
    
    const hash = window.location.hash;
    if (hash.startsWith('#story/')) {
      console.log('🔄 Resuming story from URL');
      const parts = hash.substring(7).split('/');
      if (parts.length >= 2) {
        storyIndexRef.current = parseInt(parts[0]) || 0;
        const word = decodeURIComponent(parts[1] || '');
        setCurrentWord(word);
        setShowIndicator(true);
        
        // Auto-start after a moment
        setTimeout(() => {
          setIsRunning(true);
          isRunningRef.current = true;
          showNextWord();
        }, 1000);
      }
    }

    // Cleanup on unmount
    return () => {
      console.log('🧽 Cleaning up story timers');
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [showNextWord]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isRunningRef.current) {
        console.log('Esc pressed, stopping story');
        stopStoryRef.current();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []); // Empty deps since we use refs

  // Public methods for parent component
  useImperativeHandle(ref, () => ({
    startStory,
    stopStory: stopStoryRef.current,
    isRunning: isRunningRef.current
  }));

  return (
    <div className="address-story-container">
      {/* Story indicator (top-right corner) */}
      {showIndicator && (
        <div 
          className="story-indicator"
          onClick={() => {
            console.log('🎯 Indicator clicked');
            if (isRunningRef.current) {
              stopStoryRef.current();
            } else {
              startStory();
            }
          }}
          style={{ cursor: 'pointer' }}
        >
          <span className="indicator-icon">
            {isRunning ? '▶' : '⏸'}
          </span>
          <span className="indicator-text">
            	⋋( ◕ ∧ ◕ )⋌: {isRunning ? '*' : '*'}
            {currentWord && ` - "${currentWord.substring(0, 10)}${currentWord.length > 10 ? '...' : ''}"`}
          </span>
        </div>
      )}

      {/* Debug panel - remove in production */}
      <div style={{
        position: 'fixed',
        bottom: '10px',
        right: '10px',
        background: 'rgba(0,0,0,0.8)',
        color: '#0f0',
        padding: '10px',
        fontSize: '12px',
        fontFamily: 'monospace',
        zIndex: 9999,
        border: '1px solid #0f0',
        display: showIndicator ? 'block' : 'none'
      }}>
        <div>Status: {isRunning ? 'RUNNING' : 'STOPPED'}</div>
        <div>Word: {currentWord || 'none'}</div>
        <div>Index: {storyIndexRef.current}/{storyWords.length}</div>
        <div>URL: {window.location.href}</div>
        <div>Running Ref: {isRunningRef.current ? 'true' : 'false'}</div>
      </div>
    </div>
  );
});

export default AddressBarStory;